import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { NAV, CONTACT } from "@/data/site";
import { scrollToHash, EASE } from "@/lib/scroll";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (hash) => {
    setOpen(false);
    if (pathname !== "/") { navigate("/"); setTimeout(() => scrollToHash(hash), 350); }
    else scrollToHash(hash);
  };

  return (
    <motion.header initial={{ y: -80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow] duration-500 ${scrolled || open ? "bg-[#FAFAF7]/80 backdrop-blur-xl shadow-[0_1px_0_rgba(17,21,17,0.08)]" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-5 lg:px-10 h-[76px] flex items-center justify-between">
        <Link to="/" data-testid="nav-brand-logo" onClick={() => setOpen(false)} aria-label="K&A Gartenservice Startseite"><Logo /></Link>
        <nav className="hidden lg:flex items-center gap-1 rounded-full bg-white/70 border border-[#111511]/[0.06] p-1">
          {NAV.map((n) => (
            <button key={n.id} data-testid={`nav-link-${n.id}`} onClick={() => go(n.href)} className="rounded-full px-4 py-2 text-[14px] font-medium text-[#30352F] transition-colors duration-300 hover:bg-[#111511] hover:text-white">
              {n.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <button data-testid="nav-cta-button" onClick={() => go("#anfrage")} className="hidden sm:inline-flex group items-center gap-2 rounded-full bg-[#111511] text-white pl-5 pr-4 py-2.5 text-sm font-medium transition-colors duration-300 hover:bg-[#2F5D3A]">
            Anfrage senden <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
          </button>
          <a href={CONTACT.phoneHref} target="_top" data-testid="nav-phone-mobile" aria-label="Anrufen" className="sm:hidden w-11 h-11 rounded-full bg-[#111511] text-white flex items-center justify-center"><Phone className="w-4 h-4" /></a>
          <button data-testid="nav-mobile-toggle" aria-label="Menü" onClick={() => setOpen((o) => !o)} className="lg:hidden w-11 h-11 rounded-full border border-[#111511]/15 bg-white flex items-center justify-center">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} transition={{ duration: 0.5, ease: EASE }} className="lg:hidden overflow-hidden">
            <div className="px-5 pt-4 pb-8 flex flex-col">
              {[...NAV, { label: "Kontakt & Anfrage", href: "#anfrage", id: "contact" }].map((n, i) => (
                <motion.button key={n.id} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i, duration: 0.4 }}
                  data-testid={`mobile-nav-${n.id}`} onClick={() => go(n.href)} className="text-left font-head font-semibold text-[2rem] tracking-tight py-2 text-[#111511] border-b border-[#111511]/[0.06]">
                  {n.label}
                </motion.button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
